import { OutputData } from '@editorjs/editorjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { useSearchParams } from 'react-router-dom';
import { ActionIcon, Button, Select, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import useCorporations from '../../hooks/api-calls/useCorporations';
import useFetch from '../../hooks/useFetch';
import useFilters from '../../hooks/useFilters';
import useHandleErrors from '../../hooks/useHandleErrors';
import { UserService } from '../../services/user/userService';
import DeletePopover from '../buttons/DeletePopover';
import CustomTable from '../table/CustomTable';
import EmailTemplateEditor from './EmailTemplateEditor';

const columnOrder = [
  'id',
  'name',
  'useType',
  'paymentMethod',
  'products',
  'organisingCorporations',
  'documents',
  'createdAt',
  'updatedAt',
  'templateContent'
];

export const useTypeOptions = [
  { label: 'Todos', value: '' },
  { label: 'Cliente', value: 'client' },
  { label: 'Contrato', value: 'contract' },
  { label: 'Vencimientos', value: 'expiration' }
];

const EmailTemplateList = () => {

  const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters();
  const { handleErrors } = useHandleErrors();
  const service = useMemo(() => new UserService(), []);
  const { fetchCorporations, getCorporationsList } = useCorporations({ corporation_function: 'organising' });
  const [searchParams, setSearchParams] = useSearchParams();

  // Estados
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  const [showEditor, setShowEditor] = useState(false);
  const [lastOpenedTemplateFromQuery, setLastOpenedTemplateFromQuery] = useState<string | null>(null);

  const [data, loading, error, refetch] = useFetch(useCallback(async () => {
    const response = await service.listEmailTemplates(filters);
    return response.getResponseData();
  }, [filters, service]));

  useEffect(() => {
    const templateIdToEdit = searchParams.get('editEmailTemplateId');
    if (!templateIdToEdit || templateIdToEdit === lastOpenedTemplateFromQuery) {
      return;
    }

    let isCancelled = false;

    const openTemplateFromQuery = async () => {
      try {
        const response = await (await service.getEmailTemplate(templateIdToEdit)).getResponseData();
        if (!response?.success || !response?.data) {
          handleErrors(response);
          return;
        }

        if (isCancelled) {
          return;
        }

        const template = response.data;
        setSelectedTemplate({
          id: template.id,
          name: template.name,
          template: template.templateContent || template.template || '',
          useType: template.useType || template.usageType || '',
          paymentMethod: template.paymentMethod || null,
          products: template.products || [],
          organisingCorporations: template.organisingCorporations || []
        });
        setShowEditor(true);
        setLastOpenedTemplateFromQuery(templateIdToEdit);

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('editEmailTemplateId');
        setSearchParams(nextSearchParams, { replace: true });
      } catch (error) {
        if (!isCancelled) {
          handleErrors(error);
        }
      }
    };

    openTemplateFromQuery();

    return () => {
      isCancelled = true;
    };
  }, [handleErrors, lastOpenedTemplateFromQuery, searchParams, service, setSearchParams]);

  useEffect(() => {
    fetchCorporations({ corporation_function: 'organising' });
  }, []);

  const handleEdit = (template: any) => {
    setSelectedTemplate(template);
    setShowEditor(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await (await service.deleteEmailTemplate(id)).getResponseData();
      if (response.success) {
        toast.success('Plantilla eliminada correctamente');
        refetch();
      } else {
        handleErrors(response);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSaveTemplate = async (name: string, template: OutputData | string, usageType: string, organisingCorporationIds: string[], paymentMethodId: string | null, productIds: string[]) => {
    try {
      const templateObject = {
        id: selectedTemplate?.id,
        name: name,
        useType: usageType,
        templateContent: template,
        organisingCorporationIds,
        paymentMethodId,
        productIds
      };
      let endpoint = selectedTemplate ? service.editEmailTemplate(templateObject) : service.createEmailTemplate(templateObject);
      const response = await (await endpoint).getResponseData();
      if (response.success) {
        refetch();
        setShowEditor(false);
        setSelectedTemplate(undefined);
        toast.success('Plantilla guardada correctamente');
      } else {
        handleErrors(response);
      }
    } catch (error) {
      handleErrors(error);
    }
  };

  /**
 * Order the data to match the column order
 */
  const orderedData = () => data?.data.map((row: any) => {
    const orderedRow: any = {};
    columnOrder.forEach((key) => {
      orderedRow[key] = row[key];
    });
    return orderedRow;
  });

  return (
    <div className="space-y-6">
      {data !== undefined && (
        <CustomTable
          largeTable
          id={'tutor-table'}
          columnOrder={columnOrder}
          data={orderedData()}
          isLoading={loading}
          columnsNotShown={['templateContent']}
          overrideColumns={[
            {
              key: 'useType',
              render: (row: any) => {
                return (
                  <span>
                    {useTypeOptions.find(option => option.value === row.row.original.useType)?.label || '-'}
                  </span>
                );
              },
            },
            {
              key: 'paymentMethod',
              render: (row: any) => {
                return row.row.original.paymentMethod?.name || '-';
              },
            },
            {
              key: 'products',
              render: (row: any) => {
                const products = row.row.original.products || [];
                if (!Array.isArray(products) || products.length === 0) {
                  return '-';
                }

                return products.map((product: any) => product?.name).filter(Boolean).join(', ');
              },
            },
            {
              key: 'organisingCorporations',
              render: (row: any) => {
                const organisingCorporations = row.row.original.organisingCorporations || [];
                if (!Array.isArray(organisingCorporations) || organisingCorporations.length === 0) {
                  return '-';
                }

                return organisingCorporations.map((organisingCorporation: any) => organisingCorporation?.name).filter(Boolean).join(', ');
              },
            },
            {
              key: 'documents',
              render: (row: any) => {
                return (row.row.original.documents.length);
              },
            },
          ]}
          actions={[
            {
              label: 'Edit',
              render: (row: any) => {
                return (
                  <Tooltip
                    size="sm"
                    content={'Editar tutor'}
                    placement="top"
                    color="invert"
                  >
                    <ActionIcon
                      as="span"
                      size="sm"
                      variant="outline"
                      className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                      onClick={() => handleEdit({
                        id: row.id,
                        name: row.name,
                        template: row.templateContent,
                        useType: row.useType,
                        paymentMethod: row.paymentMethod || null,
                        products: row.products || [],
                        organisingCorporations: row.organisingCorporations || []
                      })}
                    >
                      <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                    </ActionIcon>
                  </Tooltip>
                );
              },
            },
            {
              label: 'Delete',
              permissions: { group: 'tutors', permission: 'delete_tutors' },
              render: (row: any) => {
                return (
                  <Tooltip
                    size="sm"
                    content={'Eliminar'}
                    placement="top"
                    color="invert"
                  >
                    <div>
                      <DeletePopover
                        title={`Eliminar tutor`}
                        description={`¿Estás seguro de que deseas eliminar a ${row.name}?`}
                        onDelete={() => handleDelete(row.id)}
                      />
                    </div>
                  </Tooltip>
                );
              },
            },
          ]}
          filters={filters}
          updateFilters={updateFilters}
          updateFilterOrder={updateFilterOrder}
          defaultOrder={filters.filter_order || undefined}
          paginationData={{
            pageSize: filters.limit,
            currentPage: filters.page,
            pageCount: data ? data.lastPage : 1,
            totalCount: data?.totalRegisters,
            handlePagination: updatePage,
            handlePerPage: updatePageSize,
          }}
        >
          <Select
            placeholder="Tipo de uso"
            value={filters.filter_filters?.use_type ? { label: useTypeOptions.find(option => option.value === filters.filter_filters?.use_type)?.label, value: filters.filter_filters?.use_type } : null}
            onChange={(e: any) => updateFilters({ use_type: e?.value })}
            options={useTypeOptions}
            className="w-[150px]"
          />

          <Select
            placeholder="Organizadora"
            value={filters.filter_filters?.organising_corporation_id
              ? getCorporationsList().find((corporation: any) => corporation.value === filters.filter_filters?.organising_corporation_id) || null
              : null}
            onChange={(e: any) => updateFilters({ organising_corporation_id: e?.value || null })}
            options={getCorporationsList()}
            className="w-[220px]"
            clearable
          />

          <Button
            className="ml-2"
            onClick={() => setShowEditor(true)}
          >
            Crear nueva plantilla
          </Button>
        </CustomTable>
      )}

      {/* Editor de plantillas */}
      <EmailTemplateEditor
        template={selectedTemplate}
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setSelectedTemplate(undefined);
        }}
        onSave={handleSaveTemplate}
      />
    </div>
  );
};

export default EmailTemplateList;
